import {Language, LockStatus, Platform} from "@variocube/app-api";
import {EventSupportingApp, getRandomElement} from "@variocube/app-common";
import {
    AppContext,
    Avatar, Checkbox,
    classNames,
    Dialog,
    layout,
    NumberStepInput,
    PushButton,
    ThemeProvider,
    typography,
    useBoxes,
    usePlatform,
    usePreventDeactivate,
    View
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {style, stylesheet} from "typestyle";
import icon from "./icon-shutterspeed.svg";
import {render} from "react-dom";

export default class ShutterSpeedApp extends EventSupportingApp {

    constructor(platform: Platform) {
        super(platform, icon, "#840042", [Language.EN]);
        this.name = "Shutter Speed";
        this.description = "How fast can you shut?";
        this.updateLauncherItems();
    }

    render(appRoot: Element) {
        render((
            <AppContext.Provider value={{ platform: this.platform, appEventSupport: this.eventSupport }}>
                <ThemeProvider defaultTheme={{
                    colorBackground: "#2b2727",
                    colorPrimary: "#840042",
                    colorForeground: "#bababa",
                    colorInputBackground: "#fff"
                }}>
                    <ShutterSpeed />
                </ThemeProvider>
            </AppContext.Provider>
        ), appRoot);
    }
}

function ShutterSpeed() {
    const [game, setGame] = useState(false);
    const [startLevel, setStartLevel] = useState(1);
    const [hard, setHard] = useState(false);

    const handleNewGame = useCallback(() => setGame(true), []);
    const handleGameOver = useCallback(() => setGame(false), []);

    if (game) {
        return <GameView startLevel={startLevel} hard={hard} onGameOver={handleGameOver}/>;
    }
    else {
        return (
            <View className={classNames(layout.vertical, layout.center)}>
                <div className={classNames(typography.title, typography.alignCenter)}>
                    Close the boxes as fast as you can<br/> and set a new highscore.
                </div>

                <div className={classNames(layout.vertical, layout.center, layout.alignCenter, typography.alignCenter, style({ padding: "2em 0 1em 0"}))}>
                    <div>Start Level</div>
                    <NumberStepInput
                        value={startLevel}
                        min={1}
                        max={9}
                        step={1}
                        onChange={setStartLevel}
                    />
                </div>

                <div className={classNames(layout.vertical, layout.center, layout.alignCenter, typography.alignCenter, style({ paddingBottom: "2em"}))}>
                    <Checkbox checked={hard} color="choice" onChange={setHard}>Hard Mode</Checkbox>
                </div>

                <div className={typography.alignCenter}>
                    <PushButton primary onClick={handleNewGame}>
                        New Game
                    </PushButton>
                </div>
            </View>
        );
    }
}

interface GameViewProps {
    startLevel: number;
    hard: boolean;
    onGameOver: () => void;
}

function GameView({startLevel, hard, onGameOver}: GameViewProps) {
    const [level, setLevel] = useState(startLevel);
    const [score, setScore] = useState(0);
    const [over, setOver] = useState(false);
    const [open, setOpen] = useState<string[]>([]);
    const lockEventQueue = useLockEventQueue();
    const tick = useTick(100);

    console.info("GameView render");

    const platform = usePlatform();
    const boxes = useBoxes();

    const findBox = useCallback((lock: string) => boxes.filter(box => box.lock == lock).pop(), [boxes]);

    usePreventDeactivate();

    const handleLockEvent = useCallback(({lock, status}: LockStatusEvent) => {
        console.log("handleLockEvent", lock, status);
        const box = findBox(lock);
        if (box) {
            switch (status) {
                case LockStatus.Open:
                    const newOpen = new Set([...open, box.number]);
                    setOpen([...newOpen]);
                    if (newOpen.size >= (boxes.length / (hard ? 2 : 1))) {
                        setOver(true);
                    }
                    break;
                case LockStatus.Closed:
                    const newScore = score + (over ? 0 : 1);
                    const newLevel = level + ((!over && newScore % Math.trunc(boxes.length/3) == 0) ? 1 : 0);
                    setOpen(open.filter(boxNumber => boxNumber != box.number));
                    setScore(newScore);
                    setLevel(newLevel);
                    break;
            }
        }
    }, [findBox, boxes, open, score, over, level]);

    const handleOpenBox = useCallback(async () => {
        const closedBoxes = [...boxes]
            .filter(box => open.indexOf(box.number) < 0);
        const box = getRandomElement(closedBoxes);
        await platform.openLock(box.lock);
    }, [boxes, open, platform]);

    useEffect(() => {
        if (!over) {
            if (tick % Math.ceil(boxes.length/level) == 0) {
                console.log("gonna open another one.");
                requestAnimationFrame(handleOpenBox);
            }
            const lockEvent = lockEventQueue.shift();
            if (lockEvent) {
                handleLockEvent(lockEvent);
            }
        }
    }, [tick, handleOpenBox, over, boxes, level]);

    const handleGiveUp = useCallback(() => setOver(true), []);

    return (
        <View>
            <div className={classNames(layout.horizontal, layout.center)}>
                <div className={classNames(layout.fixed, layout.vertical)}>
                    <div className={classNames(typography.alignCenter, style({ padding: "2em"}))}>
                        <div>Level</div>
                        <div className={styles.score}>
                            {level}
                        </div>
                    </div>
                    <div className={classNames(typography.alignCenter, style({ padding: "2em"}))}>
                        <div>Score</div>
                        <div className={styles.score}>
                            {score}
                        </div>
                    </div>
                    <div className={classNames(typography.alignCenter, style({ padding: "2em"}))}>
                        <PushButton primary onClick={handleGiveUp}>
                            Give Up!
                        </PushButton>
                    </div>
                </div>
                <div className={classNames(layout.fill, typography.alignCenter, style({ padding: "2em"}))}>
                    <div>Open</div>
                    <div className={layout.horizontalWrap}>
                        {open.map(boxNumber => (
                            <div key={boxNumber} className={style({ padding: "0.25em"})}>
                                <Avatar primary size={2}>
                                    {boxNumber}
                                </Avatar>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Dialog open={over} onBackdropClick={onGameOver}>
                <div className={classNames(layout.vertical, styles.gameOverDialogContent)}>
                    <div className={classNames(layout.center, typography.alignCenter, typography.title)}>
                        GAME OVER
                    </div>
                    <div className={classNames(layout.center, typography.alignCenter, style({ padding: "2em"}))}>
                        <div>Score</div>
                        <div className={styles.score}>
                            {score}
                        </div>
                    </div>
                    <div className={classNames(layout.center, typography.alignCenter)}>
                        <PushButton primary onClick={onGameOver}>
                            Close
                        </PushButton>
                    </div>
                </div>
            </Dialog>
        </View>
    );
}

function useTick(intervalMs: number) {
    const [tick, setTick] = useState(0)
    useEffect(() => {
        const interval = setInterval(() => setTick(value => value + 1), intervalMs);
        return () => clearInterval(interval);
    }, [intervalMs]);
    return tick;
}

interface LockStatusEvent {
    lock: string;
    status: LockStatus;
}

function useLockEventQueue() {
    const queue = useRef<LockStatusEvent[]>([]);
    const platform = usePlatform();

    const handleLockStatus = useCallback((lock: string, status: LockStatus) => {
        console.log("adding lock status event");
        queue.current.push({lock, status});
    }, []);

    useEffect(() => {
        platform.addLockStatusListener(handleLockStatus);
        return () => platform.removeLockStatusListener(handleLockStatus);
    }, [platform, handleLockStatus]);

    return queue.current;
}


const styles = stylesheet({
    score: {
        fontSize: "2.5rem",
        fontWeight: "bold"
    },
    gameOverDialogContent: {
        padding: "4em"
    }
});